import React from "react";
import { motion } from "framer-motion";
import { Zap, Monitor, Clock, TrendingUp, UserPlus, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { fadeIn, fadeInUp, fadeInUpSmall } from "../../lib/animations";

const benefits = [
  {
    id: "benefit-speed",
    icon: Zap,
    title: "Generate ads and content in seconds",
    desc: "AI creates optimized ad copy, images, and video from any listing URL.",
    color: "#39FF14",
  },
  {
    id: "benefit-notech",
    icon: Monitor,
    title: "Launch campaigns without technical setup",
    desc: "No Ads Manager experience needed. One click to go live on Facebook and Instagram.",
    color: "#8B5CF6",
  },
  {
    id: "benefit-central",
    icon: TrendingUp,
    title: "Manage everything in one place",
    desc: "Campaigns, leads, follow-ups, and reporting — all in a single dashboard.",
    color: "#3B82F6",
  },
  {
    id: "benefit-time",
    icon: Clock,
    title: "Save hours every week",
    desc: "Automate the repetitive work so you can focus on showings and closings.",
    color: "#F59E0B",
  },
  {
    id: "benefit-scale",
    icon: UserPlus,
    title: "Scale your marketing without hiring",
    desc: "AI handles what used to take a marketing team — at a fraction of the cost.",
    color: "#EC4899",
  },
];

export function BenefitsSection({ navigate }) {
  return (
    <section className="relative z-10 px-6 md:px-12 lg:px-24 py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={fadeInUpSmall.initial}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">What You Get</h2>
          <p className="text-gray-600 text-base md:text-lg">Built for agents who want results, not busywork</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {benefits.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0.99, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-start gap-4 p-5 rounded-xl border border-gray-200 bg-white hover:shadow-md transition-all"
            >
              <div
                className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <item.icon className="w-5 h-5" style={{ color: item.color }} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/pricing')}
            className="bg-[#39FF14] text-black hover:bg-[#32E012] font-semibold px-8 py-5 text-base h-auto shadow-md"
            data-testid="benefits-cta"
          >
            Get Founding Member Access <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-gray-400 text-sm mt-3">Lock in your founding price before spots run out.</p>
        </div>
      </div>
    </section>
  );
}
